// src/v3/Price/Price.tsx
import { cx } from "class-variance-authority";
import { useContext } from "react";

// src/v3/Price/CurrencyProvider.tsx
import {
  I18nContext,
  I18nManager
} from "@shopify/react-i18n";
import { createContext, useMemo, useRef } from "react";
import { jsx } from "react/jsx-runtime";
var CurrencyContext = createContext({
  fallbackCurrencyCode: "USD",
  freeMessage: ""
});
function CurrencyProvider({
  children,
  country,
  fallbackCurrencyCode = "USD",
  fallbackLocale,
  freeMessage = "",
  locale,
  onI18nManagerError
}) {
  const onI18nManagerErrorRef = useRef(onI18nManagerError);
  const currencyManager = useMemo(() => {
    return new I18nManager({
      country,
      fallbackLocale,
      locale,
      onError: onI18nManagerErrorRef.current
    });
  }, [country, fallbackLocale, locale]);
  const currencyContextValue = useMemo(
    () => ({ fallbackCurrencyCode, freeMessage }),
    [fallbackCurrencyCode, freeMessage]
  );
  return /* @__PURE__ */ jsx(I18nContext.Provider, {
    value: currencyManager,
    children: /* @__PURE__ */ jsx(CurrencyContext.Provider, {
      value: currencyContextValue,
      children
    })
  });
}

// src/v3/Price/FormattedPrice.tsx
import { useI18n } from "@shopify/react-i18n";
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
function FormattedPrice({
  amount,
  className = "",
  currencyCode,
  currencyCodeStyles = "",
  fractionDigits,
  showCurrency = false
}) {
  const [i18n] = useI18n();
  const formatted = i18n.formatCurrency(amount, {
    currency: currencyCode,
    form: showCurrency ? "explicit" : "short",
    precision: fractionDigits
  });
  const parts = formatted.split(" ");
  if (parts.length > 1 && currencyCodeStyles && showCurrency) {
    const [part, ...rest] = parts;
    return /* @__PURE__ */ jsxs(Fragment, {
      children: [
        /* @__PURE__ */ jsxs("span", {
          className,
          children: [
            part,
            "\xA0"
          ]
        }),
        /* @__PURE__ */ jsx2("span", {
          className: currencyCodeStyles,
          children: rest
        })
      ]
    });
  }
  return /* @__PURE__ */ jsx2(Fragment, {
    children: formatted
  });
}
FormattedPrice.displayName = "FormattedPrice";

// src/v3/Price/Price.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function Price({
  currencyCode,
  currencyCodeStyles,
  className,
  discount = 0,
  discountStyles,
  discountedPrice,
  price,
  priceCompare = false,
  stacked = false,
  showCurrency,
  showDecimalsForWholeDollars = false,
  showZeroAsFree = true,
  ...props
}) {
  const { fallbackCurrencyCode, freeMessage } = useContext(CurrencyContext);
  const selectedCurrencyCode = currencyCode != null ? currencyCode : fallbackCurrencyCode;
  const parsedPrice = Number(price);
  const _discountedPrice = discountedPrice ? { ...discountedPrice } : {
    amount: Math.max(parsedPrice - discount, 0),
    currencyCode: selectedCurrencyCode
  };
  const isFree = showZeroAsFree && freeMessage && _discountedPrice.amount === 0;
  const basePriceFractionDigits = showDecimalsForWholeDollars || parsedPrice % 1 !== 0 ? 2 : 0;
  const discountedPriceFractionDigits = showDecimalsForWholeDollars || _discountedPrice.amount % 1 !== 0 ? 2 : 0;
  const comparingPrices = priceCompare && parsedPrice - _discountedPrice.amount > 0;
  return /* @__PURE__ */ jsxs2("span", {
    className: cx(className, {
      block: priceCompare && stacked,
      "ui-inline-flex ui-gap-2": priceCompare && !stacked
    }),
    "data-cy": "sormus-price",
    ...props,
    children: [
      /* @__PURE__ */ jsx3("span", {
        "data-cy": discount && priceCompare ? "product-detail-discount-price" : "product-detail-price",
        children: isFree ? freeMessage : /* @__PURE__ */ jsx3(FormattedPrice, {
          amount: _discountedPrice.amount,
          currencyCode: _discountedPrice.currencyCode,
          currencyCodeStyles,
          fractionDigits: discountedPriceFractionDigits,
          showCurrency
        })
      }),
      comparingPrices && !currencyCodeStyles ? /* @__PURE__ */ jsx3("del", {
        className: cx("ui-line-through", discountStyles, {
          "ui-block": stacked
        }),
        "data-cy": "product-detail-regular-price",
        children: /* @__PURE__ */ jsx3(FormattedPrice, {
          amount: parsedPrice,
          currencyCode: selectedCurrencyCode,
          currencyCodeStyles,
          fractionDigits: basePriceFractionDigits,
          showCurrency
        })
      }) : null,
      comparingPrices && currencyCodeStyles ? /* @__PURE__ */ jsx3("span", {
        className: cx(discountStyles, {
          "ui-block": stacked
        }),
        "data-cy": "product-detail-regular-price",
        children: /* @__PURE__ */ jsx3(FormattedPrice, {
          amount: parsedPrice,
          className: "ui-line-through",
          currencyCode: selectedCurrencyCode,
          currencyCodeStyles,
          fractionDigits: basePriceFractionDigits,
          showCurrency
        })
      }) : null
    ]
  });
}
Price.displayName = "Price";
export {
  CurrencyProvider,
  Price
};
//# sourceMappingURL=index.mjs.map